import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { VirtuosoGrid } from 'react-virtuoso'
import type { Card } from '@/types'
import { CardThumbnail, CurrentCardMarker, type CardType } from './CardThumbnail'
import { sortLids } from '@/utils/lid'
import './CardCarousel.css'

type CarouselMode = 'children' | 'siblings' | 'backlinks'

// Item type for carousel - can be a card or a "current" marker
type CarouselItem =
  | { type: 'card'; card: Card }
  | { type: 'current'; lid: string }

interface CardCarouselProps {
  children: Card[]
  siblings: Card[]
  backlinks: Card[]
  currentLid: string
  onNavigate: (lid: string) => void
  showHelp: boolean
  onToggleHelp: () => void
}

const VIRTUALIZATION_THRESHOLD = 15

export function CardCarousel({
  children,
  siblings,
  backlinks,
  currentLid,
  onNavigate,
  showHelp,
  onToggleHelp,
}: CardCarouselProps) {
  const [mode, setMode] = useState<CarouselMode>(() => {
    // Default to the tab that has content
    if (children.length > 0) return 'children'
    if (siblings.length > 0) return 'siblings'
    if (backlinks.length > 0) return 'backlinks'
    return 'children'
  })

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Build siblings list with current card marker inserted at correct position
  const siblingsWithCurrent = useMemo((): CarouselItem[] => {
    // Create a list with all siblings + current lid for sorting
    const allLids = [...siblings.map(s => s.lid), currentLid]
    const sortedLids = sortLids(allLids)

    // Build the items array in sorted order
    return sortedLids.map(lid => {
      if (lid === currentLid) {
        return { type: 'current' as const, lid }
      }
      const card = siblings.find(s => s.lid === lid)!
      return { type: 'card' as const, card }
    })
  }, [siblings, currentLid])

  // Get current items and type based on mode
  const getCurrentItems = useCallback((): { items: CarouselItem[]; type: CardType } => {
    switch (mode) {
      case 'children':
        return { items: children.map(card => ({ type: 'card' as const, card })), type: 'child' }
      case 'siblings':
        return { items: siblingsWithCurrent, type: 'sibling' }
      case 'backlinks':
        return { items: backlinks.map(card => ({ type: 'card' as const, card })), type: 'backlink' }
    }
  }, [mode, children, siblingsWithCurrent, backlinks])

  const { items, type } = getCurrentItems()

  // Handle mouse wheel for horizontal scrolling (non-passive to allow preventDefault)
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault()
        container.scrollLeft += e.deltaY
      }
    }

    container.addEventListener('wheel', handleWheel, { passive: false })
    return () => container.removeEventListener('wheel', handleWheel)
  }, [items.length])

  // Keyboard shortcut for tab switching (Shift+C/S/B)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if in input/textarea
      const target = e.target as HTMLElement
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable
      ) {
        return
      }

      // Shift key shortcuts for tabs
      if (e.shiftKey) {
        switch (e.key.toUpperCase()) {
          case 'C':
            e.preventDefault()
            setMode('children')
            break
          case 'S':
            e.preventDefault()
            setMode('siblings')
            break
          case 'B':
            e.preventDefault()
            setMode('backlinks')
            break
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Scroll to center when mode changes and there are items
  useEffect(() => {
    if (scrollContainerRef.current && items.length > 0) {
      scrollContainerRef.current.scrollLeft = 0
    }
  }, [mode, items.length])

  const useVirtualization = items.length > VIRTUALIZATION_THRESHOLD

  const renderItem = (index: number) => {
    const item = items[index]
    if (item.type === 'current') {
      return <CurrentCardMarker key={item.lid} lid={item.lid} />
    }
    return (
      <CardThumbnail
        key={item.card.lid}
        card={item.card}
        type={type}
        onClick={() => onNavigate(item.card.lid)}
      />
    )
  }

  const hasAnyCards = children.length > 0 || siblings.length > 0 || backlinks.length > 0

  if (!hasAnyCards) {
    return null
  }

  return (
    <div className="card-carousel">
      <div className="carousel-tabs">
        <button
          className={`carousel-tab ${mode === 'children' ? 'carousel-tab--active' : ''}`}
          onClick={() => setMode('children')}
          disabled={children.length === 0}
        >
          Children ({children.length})
        </button>
        <button
          className={`carousel-tab ${mode === 'siblings' ? 'carousel-tab--active' : ''}`}
          onClick={() => setMode('siblings')}
          disabled={siblings.length === 0}
        >
          Siblings ({siblings.length})
        </button>
        <button
          className={`carousel-tab ${mode === 'backlinks' ? 'carousel-tab--active' : ''}`}
          onClick={() => setMode('backlinks')}
          disabled={backlinks.length === 0}
        >
          Backlinks ({backlinks.length})
        </button>

        <button
          className="carousel-help-btn"
          onClick={onToggleHelp}
          title="Keyboard shortcuts (Shift+?)"
        >
          ?
        </button>
      </div>

      {/* Keyboard shortcuts help modal */}
      {showHelp && (
        <div className="shortcuts-modal-overlay" onClick={onToggleHelp}>
          <div className="shortcuts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="shortcuts-modal__header">
              <h3>Keyboard Shortcuts</h3>
              <button className="shortcuts-modal__close" onClick={onToggleHelp}>×</button>
            </div>
            <div className="shortcuts-modal__content">
              <div className="shortcuts-group">
                <h4>Navigation</h4>
                <div className="shortcut-row"><kbd>↑</kbd> <kbd>k</kbd> Previous sibling</div>
                <div className="shortcut-row"><kbd>↓</kbd> <kbd>j</kbd> Next sibling</div>
                <div className="shortcut-row"><kbd>←</kbd> <kbd>h</kbd> Parent card</div>
                <div className="shortcut-row"><kbd>→</kbd> <kbd>l</kbd> First child</div>
              </div>
              <div className="shortcuts-group">
                <h4>Tabs</h4>
                <div className="shortcut-row"><kbd>Shift</kbd>+<kbd>C</kbd> Children</div>
                <div className="shortcut-row"><kbd>Shift</kbd>+<kbd>S</kbd> Siblings</div>
                <div className="shortcut-row"><kbd>Shift</kbd>+<kbd>B</kbd> Backlinks</div>
              </div>
              <div className="shortcuts-group">
                <h4>Actions</h4>
                <div className="shortcut-row"><kbd>Shift</kbd>+<kbd>H</kbd> Home</div>
                <div className="shortcut-row"><kbd>Shift</kbd>+<kbd>A</kbd> Add Related Card</div>
                <div className="shortcut-row"><kbd>Shift</kbd>+<kbd>L</kbd> List View</div>
                <div className="shortcut-row"><kbd>Shift</kbd>+<kbd>V</kbd> Canvas View</div>
                <div className="shortcut-row"><kbd>Shift</kbd>+<kbd>?</kbd> Show this help</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="carousel-content">
        {items.length === 0 ? (
          <div className="carousel-empty">
            No {mode} found
          </div>
        ) : useVirtualization ? (
          <VirtuosoGrid
            className="carousel-virtuoso"
            totalCount={items.length}
            itemContent={renderItem}
            listClassName="carousel-list"
            itemClassName="carousel-item-wrapper"
          />
        ) : (
          <div className="carousel-track" ref={scrollContainerRef}>
            {items.map((_, index) => renderItem(index))}
          </div>
        )}
      </div>
    </div>
  )
}

export { CardThumbnail } from './CardThumbnail'
